import { RotateCcw } from 'lucide-react';
import { Button } from '@/shared/components';
import type {
  GuestCommunicationChannel,
  PropertyCommunicationContent,
  PropertyCommunicationMessageResponse,
} from '@/shared/api';
import { hasOverride } from './catalogModel';
import { CommunicationMessageHeader } from './CommunicationMessageHeader';
import { ChannelSwitch, LanguageSwitch, SystemRow, type Mark } from './EditorControls';
import { EditorField } from './EditorField';
import { bodyHint, languageHint } from './editorHints';
import { MESSAGE_PRESENTATION } from './messagePresentation';
import { contentKey, fieldsFor, own, type Change, type FieldKey, type Lang } from './editorModel';

interface Props {
  message: PropertyCommunicationMessageResponse;
  saved?: PropertyCommunicationMessageResponse;
  editable: boolean;
  /** Im Vollbild auf dem Telefon trägt der Kopf des Sheets den Anlass. */
  showHeader?: boolean;
  channel: GuestCommunicationChannel;
  lang: Lang;
  pending: Change[];
  examples: Record<string, string>;
  focus: { field: FieldKey; request: number } | null;
  onFocusHandled: () => void;
  onChannelChange: (channel: GuestCommunicationChannel) => void;
  onLangChange: (lang: Lang) => void;
  onContentChange: (patch: Partial<PropertyCommunicationContent>) => void;
  onResetChannel: () => void;
}

/** Der gewählte Anlass: Kanal, Sprache und die Felder genau dieser Fassung. */
export function CommunicationEditor(props: Props) {
  const { message, channel, lang } = props;
  const content = message.channels.find((item) => item.channel === channel)!;
  const saved = props.saved?.channels.find((item) => item.channel === channel);
  const mine = props.pending.filter((change) => change.key === message.key);
  const channelMarks = Object.fromEntries(
    message.channels.map((item) => [
      item.channel,
      markOf(
        mine.some((c) => c.channel === item.channel),
        hasOverride(item),
      ),
    ]),
  ) as Partial<Record<GuestCommunicationChannel, Mark>>;
  const langMarks = Object.fromEntries(
    (['De', 'En'] as const).map((item) => [
      item,
      markOf(
        mine.some((c) => c.channel === channel && c.lang === item),
        fieldsFor(channel, message.key).some(
          (field) => own(content[contentKey(field.key, item)]) !== null,
        ),
      ),
    ]),
  ) as Partial<Record<Lang, Mark>>;

  return (
    <article
      aria-label={MESSAGE_PRESENTATION[message.key].title}
      className="border-line/50 bg-card shadow-card space-y-4 rounded-lg border p-4 sm:p-5"
    >
      {props.showHeader !== false && <CommunicationMessageHeader message={message} />}
      <ChannelSwitch
        channels={message.channels.map((item) => item.channel)}
        active={channel}
        marks={channelMarks}
        onChange={props.onChannelChange}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <LanguageSwitch active={lang} marks={langMarks} onChange={props.onLangChange} />
        <span className="text-ink-soft text-xs">
          {languageHint(message.key === 'PostStay', channel, content, lang)}
        </span>
      </div>
      {props.editable ? (
        fieldsFor(channel, message.key).map((spec) => (
          <EditorField
            key={`${message.key}-${channel}-${spec.key}-${lang}`}
            spec={spec}
            lang={lang}
            channel={content}
            saved={saved}
            examples={props.examples}
            hint={spec.key === 'body' ? bodyHint(channel, message.key === 'PostStay') : undefined}
            focusRequest={props.focus?.field === spec.key ? props.focus.request : 0}
            onFocused={props.onFocusHandled}
            onChange={(key, value) => props.onContentChange({ [key]: value })}
          />
        ))
      ) : (
        <p className="border-line/60 text-ink-soft rounded-md border border-dashed p-3 text-sm">
          Nur Ansicht – Schalter und Felder erscheinen mit dem Recht „Gasterlebnis bearbeiten“.
        </p>
      )}
      <SystemRow messageKey={message.key} channel={channel} />
      {props.editable && hasOverride(content) && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-auto min-h-11 whitespace-normal text-left"
          leftIcon={<RotateCcw className="size-4 shrink-0" />}
          onClick={props.onResetChannel}
        >
          Ganze {channel === 'Email' ? 'E-Mail' : 'OTA-Nachricht'} auf StayOn-Standard zurücksetzen
        </Button>
      )}
    </article>
  );
}

const markOf = (unsaved: boolean, custom: boolean): Mark =>
  unsaved ? 'unsaved' : custom ? 'custom' : null;
